<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useCustomerConfig } from '@peanuds/app-shell';
import { useTranslation } from 'i18next-vue';
import PreOrderDialog from './PreOrderDialog.vue';
import defaultVideoSrc from '@peanuds/ui/assets/media/cards-slide.mp4';

const { t } = useTranslation();
const videoRef = ref<HTMLVideoElement | null>(null);
const showPreOrderDialog = ref(false);
const { links, media } = useCustomerConfig();
const router = useRouter();

const resolvedPromoMedia = computed(() => {
  return media.value.totalBalancePromoMedia ?? { src: defaultVideoSrc, type: 'video' as const };
});

const playVideo = () => {
  videoRef.value?.play().catch(() => {
    // Ignore autoplay errors
  });
};

const openPromo = () => {
  const routeName = links.value.totalBalancePromoRouteName;
  if (routeName === undefined) {
    showPreOrderDialog.value = true;
    return;
  }

  void router.push({ name: routeName });
};

watch(
  resolvedPromoMedia,
  async () => {
    await nextTick();
    playVideo();
  },
  { immediate: true },
);
</script>

<template>
  <div class="promo-area">
    <button
      type="button"
      class="promo-area__trigger"
      :aria-label="t('totalBalance.promo.title')"
      @click="openPromo"
    >
      <video
        v-if="resolvedPromoMedia.type === 'video'"
        ref="videoRef"
        class="promo-area__media"
        :src="resolvedPromoMedia.src"
        loop
        muted
        playsinline
      />
      <img v-else class="promo-area__media" :src="resolvedPromoMedia.src" alt="" />
      <span class="promo-area__variants">{{ t('totalBalance.promo.variants') }}</span>
      <span class="promo-area__title">{{ t('totalBalance.promo.title') }}</span>
    </button>
    <PreOrderDialog v-model:visible="showPreOrderDialog" />
  </div>
</template>

<style lang="scss" scoped>
.promo-area {
  &__trigger {
    position: relative;
    display: block;
    width: 100%;
    height: 128px;
    overflow: hidden;
    border: none;
    border-radius: $radius-8;
    // The card is always photo-backed with white text, so the fallback behind
    // the media must stay dark in both themes - no theme token stays dark.
    background-color: #30303a;
    padding: 0;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.01);
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        180deg,
        rgb(0 0 0 / 55%) 0%,
        rgb(0 0 0 / 0%) 38%,
        rgb(0 0 0 / 0%) 46%,
        rgb(0 0 0 / 70%) 100%
      );
    }
  }

  &__media {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__variants {
    position: absolute;
    top: $spacing-sm;
    left: $spacing-sm;
    max-width: 9ch;
    font-size: $font-size-xs;
    font-weight: $font-weight-semibold;
    line-height: 1.12;
    letter-spacing: $tracking-body;
    color: $color-base-white;
    opacity: 0.61;
  }

  &__title {
    position: absolute;
    right: $spacing-sm;
    bottom: $spacing-lg;
    left: $spacing-sm;
    text-align: center;
    font-size: $font-size-md;
    font-weight: $font-weight-semibold;
    letter-spacing: $tracking-body;
    color: $color-base-white;
  }
}
</style>
